import React, { useEffect, useMemo, useState } from 'react';
import { X, ShoppingBag, Printer, Calendar, Check, ChevronRight } from 'lucide-react';
import { Recipe, useStore } from '../../store/useStore';
import { getWeekInfo } from '../../lib/weekHelper';
import { toFractionHTML, parseIngredient } from '../../lib/ingredientParser';
// Optional: install and import `pluralize` for better plural/singular handling

interface MarketListModalProps {
  isOpen: boolean;
  onClose: () => void;
  weeklyPlan: any;
}

const MarketListModal: React.FC<MarketListModalProps> = ({
  isOpen,
  onClose,
  weeklyPlan
}) => {
  const [selectedWeeks, setSelectedWeeks] = useState<number>(1);
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const [weekId, setWeekId] = useState<string>("");
  const fetchRecipeById = useStore((state) => state.fetchRecipeById);
  const groupIngredients = useStore((state) => state.groupIngredients);


  useEffect(() => {
    if (selectedWeeks == 2) {
      let weekIdTmp = getWeekInfo();
      // add 1 to weekId;
      let [week, year] = weekIdTmp.split("_");
      weekIdTmp = `${Number(week) + 1}_${year}`;
      setWeekId(weekIdTmp);
    } else if (selectedWeeks == 3) {
      let weekIdTmp = getWeekInfo();
      // add 2 to weekId;
      let [week, year] = weekIdTmp.split("_");
      weekIdTmp = `${Number(week) + 2}_${year}`;
      setWeekId(weekIdTmp);
    } else if (selectedWeeks == 4) {
      let weekIdTmp = getWeekInfo();
      // add 3 to weekId;
      let [week, year] = weekIdTmp.split("_");
      weekIdTmp = `${Number(week) + 3}_${year}`;
      setWeekId(weekIdTmp);
    } else {
      let weekIdTmp = getWeekInfo();
      setWeekId(weekIdTmp);
    }
  }, [selectedWeeks]);

  const ingredients = useMemo(() => {
    const weeksToInclude = weeklyPlan[weekId];
    // console.log("Weeks to include: ", weekId, weeklyPlan, weeksToInclude);

    const allIngredients = weeksToInclude?.flatMap((day: any) => {
      const meals = [day.meals?.breakfast, day.meals?.lunch, day.meals?.dinner]
        .filter(meal => {
          let hasIngredients = false;
          if (Array.isArray(meal)) {
            meal.forEach((each) => {
              const recipe = fetchRecipeById(each?.recipeId);
              if (recipe?.ingredients?.length > 0) hasIngredients = true;
            });
          }
          return hasIngredients;
        });

      return meals.flatMap(meal => {
        const ingredients: any[] = [];
        if (Array.isArray(meal)) {
          meal.forEach((each) => {
            const recipe = fetchRecipeById(each?.recipeId);
            if (recipe?.ingredients) ingredients.push(...recipe.ingredients);
          });
        }
        return ingredients;
      });
    });

    const groupedIngredientsObj = allIngredients?.reduce((acc: any, ingredient: string) => {
      const { quantity, unit, item, matchType, metric, altQuantity } = parseIngredient(ingredient);

      const key = item.trim();
      // let keys = Object.keys(acc);
      if (!acc[key]) {
        acc[key] = {
          quantity,
          unit,
          item,
          original: [ingredient],
          matchType,
          metric,
          altQuantity,
          quantityFraction: toFractionHTML(altQuantity)
        };
      } else {
        acc[key].quantity += quantity;
        acc[key].original.push(ingredient);
        acc[key].quantityFraction = toFractionHTML(acc[key].quantity)
      }
      return acc;
    }, {});


    // const measuredUnits = ['g', 'kg', 'mg', 'ml', 'l', 'tbsp', 'tsp', 'oz', 'cup'];

    const groupedIngredients = groupedIngredientsObj
      ? Object.values(groupedIngredientsObj).sort((a: any, b: any) => a.item.localeCompare(b.item))
      : [];

    console.log(`Week ${weekId}: ${groupedIngredients}`);
    return groupedIngredients;
  }, [weeklyPlan, selectedWeeks, weekId]);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const content = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Shopping List</title>
          <style>
            body { font-family: system-ui, -apple-system, sans-serif; line-height: 1.5; padding: 2rem; }
            h1 { color: #333; }
            .date { color: #666; margin-bottom: 2rem; }
            .category { margin-bottom: 1.5rem; }
            .category-title { font-weight: bold; margin-bottom: 0.5rem; }
            .item { margin-bottom: 0.5rem; line-height: 1 }
            @media print {
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          <h1>Shopping List for ${selectedWeeks} ${selectedWeeks === 1 ? 'Week' : 'Weeks'}</h1>
          <div class="date">
            ${new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })}
          </div>
          <div class="items ">
            ${ingredients.map((ing: any) => `
              <div class="item">
                □ ${ing.quantity} x ${ing.unit ? ` ${ing.unit} ` : ' '}${ing.item}
              </div>
            `).join('')}
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
  };

  const toggleItem = (itemKey: string) => {
    setCheckedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemKey)) {
        newSet.delete(itemKey);
      } else {
        newSet.add(itemKey);
      }
      return newSet;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <div className="flex items-center">
            <ShoppingBag className="text-secondary mr-2" size={24} />
            <div>
              <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
                Shopping List
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Time to shop, guided by our pre-calculated shopping list
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <Printer size={20} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-4">
            <Calendar size={20} className="text-gray-500 dark:text-gray-400" />
            <div className="flex gap-2">
              {[1, 2, 3, 4].map(weeks => (
                <button
                  key={weeks}
                  onClick={() => setSelectedWeeks(weeks)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${selectedWeeks === weeks
                    ? 'bg-secondary-700 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                >
                  {/* {weeks} {weeks === 1 ? 'Week' : 'Weeks'} */}
                  {weeks === 4 ? 'Week 4' : weeks === 2 ? `Next Week` : weeks === 3 ? "Week 3" : "This Week"}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 modal-content">
          <div className="space-y-4">
            {ingredients.map((ing: any, index) => {
              const itemKey = `${ing.quantity}${ing.unit}${ing.item}`;
              return (
                <div
                  key={index}
                  className="flex items-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                >
                  <input
                    type="checkbox"
                    checked={checkedItems.has(itemKey)}
                    onChange={() => toggleItem(itemKey)}
                    className="h-5 w-5 text-secondary rounded border-gray-300 dark:border-gray-600 focus:ring-secondary accent-secondary-400 cursor-pointer"
                  />
                  <span className={`ml-3 text-gray-700 ${ing.metric ? "" : "gap-2"} flex dark:text-gray-300 ${checkedItems.has(itemKey) ? 'line-through text-gray-400 dark:text-gray-500' : ''
                    }`}>
                    <div>
                      {Number(ing.quantityFraction) !== 0 && <span dangerouslySetInnerHTML={{ __html: ing.quantityFraction }}></span>}
                      {ing.metric && (
                        <>
                          {["tbsp", "tsp", "tins", "tin", "slice", "slices", "cup", "cups"].includes(ing.metric) ? (
                            <span className="ml-1">{ing.metric}</span>
                          ) : (
                            ing.metric
                          )}{" "}
                          of
                        </>
                      )}{" "}
                      {ing.item}
                    </div>

                  </span>
                </div>
              );
            })}
          </div>

          {ingredients.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">
                No ingredients found. Add some meals to your plan first!
              </p>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {ingredients.length} items total · {checkedItems.size} checked
            </p>
            <div className="relative w-full mt-4">
              <button
                onClick={() => window.open('https://ezyvibes.com/disclaimer', '_blank')}
                className="flex items-center justify-between w-full bg-yellow-100 hover:bg-yellow-200 text-yellow-900 px-4 py-2 rounded-lg mb-2 transition-colors duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <span className="truncate font-medium">
                  ⚠️ Shopping Reminder:
                  <span className="hidden sm:inline"> This list is based on selected recipes. Please check all product labels for allergens.</span>
                </span>
                <span className="flex items-center gap-1 text-sm text-yellow-800 group-hover:text-yellow-900 transition-colors">
                  Details here <ChevronRight className="w-4 h-4" />
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketListModal;